import { StaticLabel } from "PosterEditor/model/poster-project";
import { drawSelectedRect, MapElement } from "../canvas-drawing";
import { Rect } from "PosterEditor/model/poster-types";
import { Point } from "PosterEditor/model/poster-types";
import { pointIsInsideRect } from "../point-utils";
import { convertToRGBA } from "../color-utils";
import { MapDrawingConfig } from "../canvas-drawing";
import { rectWithPadding } from "../rect-utils";
import {
  nearestVerticalSnapLine,
  nearestHorzontalSnapLine,
  SnapLine,
} from "../rect-snapping";

export const drawStaticLabels = ({
  map,
  canvasElement,
  project,
  pointerLocation,
  elementToHighlight,
  selectedElement,
}: MapDrawingConfig): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  const ctx = canvasElement.current.getContext("2d");
  const result = drawLabels(
    project.staticLabels,
    map,
    ctx,
    pointerLocation,
    elementToHighlight,
    selectedElement?.staticLabel
  );
  if (result.labelUnderCursor) {
    const mapElement: MapElement = {
      type: "staticLabel",
      rect: result.labelUnderCursor.rect,
      element: result.labelUnderCursor.label,
    };
    return {
      elementUnderPointerLocation: mapElement,
      snapLines: result.snapLines,
    };
  }
  return {
    elementUnderPointerLocation: undefined,
    snapLines: result.snapLines,
  };
};

const drawLabels = (
  labels: StaticLabel[],
  map: any,
  ctx: CanvasRenderingContext2D,
  pointerLocation: Point | undefined,
  elementToHighlight: MapElement | undefined,
  selectedLabel: StaticLabel | undefined
): {
  labelUnderCursor:
    | { label: StaticLabel; rect: Rect; point: Point }
    | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horiztonal: SnapLine | undefined;
  };
} => {
  if (!labels) {
    return {
      labelUnderCursor: undefined,
      snapLines: { vertical: undefined, horiztonal: undefined },
    };
  }
  const points = labels.map((label) => label.position);

  let labelUnderCursor:
    | { label: StaticLabel; rect: Rect; point: Point }
    | undefined = undefined;

  let rects: Rect[] = [];
  let selectedRect: Rect | undefined;

  points.forEach((point, index) => {
    const label = labels[index];
    const isSelected = label.id === selectedLabel?.id;
    const labelDrawn = drawLabel(
      ctx,
      label,
      pointerLocation,
      elementToHighlight,
      isSelected
    );
    //Match this label.rect against all order label rects
    if (labelDrawn) {
      if (isSelected) {
        selectedRect = labelDrawn.rect;
      } else {
        rects.push(labelDrawn.rect);
      }
    }
    if (labelDrawn && labelDrawn.isUnderCursor) {
      labelUnderCursor = { label, rect: labelDrawn.rect, point };
    }
  });

  if (selectedRect) {
    const contextRect = ctx.canvas.getBoundingClientRect();
    rects.push({
      x: 0,
      y: 0,
      width: contextRect.width,
      height: contextRect.height,
    });
    const snapVertical = nearestVerticalSnapLine(selectedRect, rects);

    const snapHorizontal = nearestHorzontalSnapLine(selectedRect, rects);

    if (snapVertical) {
      drawVerticalSnapLine(ctx, snapVertical);
    }
    if (snapHorizontal) {
      drawHorizontalSnapLine(ctx, snapHorizontal);
    }
    return {
      labelUnderCursor,
      snapLines: { vertical: snapVertical, horiztonal: snapHorizontal },
    };
  }

  return {
    labelUnderCursor,
    snapLines: { vertical: undefined, horiztonal: undefined },
  };
};

const drawVerticalSnapLine = (
  ctx: CanvasRenderingContext2D,
  snapLine: SnapLine
) => {
  ctx.beginPath();
  ctx.strokeStyle = "white";
  ctx.lineWidth = 1;
  ctx.moveTo(snapLine.position, snapLine.start);
  ctx.lineTo(snapLine.position, snapLine.end);
  ctx.stroke();
};

const drawHorizontalSnapLine = (
  ctx: CanvasRenderingContext2D,
  snapLine: SnapLine
) => {
  ctx.beginPath();
  ctx.strokeStyle = "white";
  ctx.lineWidth = 2;
  ctx.moveTo(snapLine.start, snapLine.position);
  ctx.lineTo(snapLine.end, snapLine.position);
  ctx.stroke();
};

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
};

const emptyLabelString = "label";

const drawLabel = (
  ctx: CanvasRenderingContext2D,
  label: StaticLabel,
  pointerLocation: Point | undefined,
  elementToHighlight: MapElement | undefined,
  isSelected: boolean
): { rect: Rect; isUnderCursor: boolean } | undefined => {
  if (!label || !label.style) return undefined;

  const {
    text,
    style: {
      backgroundColor,
      backgroundOpacity,
      padding,
      font,
      textColor,
      fontSize: relativeFontSize,
      borderRadius,
    },
  } = label;

  const labelText = text.length > 0 ? text : emptyLabelString;

  let isLabelDimmed = false;
  if (
    elementToHighlight?.type === "staticLabel" &&
    (elementToHighlight?.element as StaticLabel).id !== label.id
  ) {
    isLabelDimmed = true;
  }
  ctx.globalAlpha = 1; //isLabelDimmed ? 0.2 : 1;

  ctx.setLineDash([]);

  const fontSize = relativeFontSize * ctx.canvas.width;

  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const textSize = textSizeInContext(labelText, font, fontSize, ctx);
  const textWidth = textSize.width;
  const textHeight = textSize.height;

  const x = label.position.x * ctx.canvas.width;
  const y = label.position.y * ctx.canvas.height;
  const paddingHorizontal = padding * ctx.canvas.width;
  const paddingVertical = padding * ctx.canvas.width;
  const labelPosition = { x, y };

  let point: Point;
  if (label.style.alignment === "left") {
    point = labelPosition;
  } else if (label.style.alignment === "right") {
    point = { x: labelPosition.x - textWidth, y: labelPosition.y };
  } else {
    point = { x: labelPosition.x - textWidth / 2, y: labelPosition.y };
  }

  const boxX = point.x - paddingHorizontal;
  const boxY = point.y - paddingVertical;

  const boxWidth = textWidth + paddingHorizontal * 2;
  const boxHeight = textHeight + paddingVertical * 2;

  let isHighlighted = false;
  if (pointerLocation) {
    let rect: Rect = { x: boxX, y: boxY, width: boxWidth, height: boxHeight };
    if (pointIsInsideRect(pointerLocation, rect)) {
      isHighlighted = true;
    }
  }
  ctx.save();

  if (backgroundColor) {
    const opacity = backgroundOpacity ?? 1;
    ctx.fillStyle = convertToRGBA(backgroundColor, opacity);
    drawRoundedRect(ctx, boxX, boxY, boxWidth, boxHeight, borderRadius);
    ctx.fill();
  }

  const rect = { x: boxX, y: boxY, width: boxWidth, height: boxHeight };

  ctx.fillStyle = textColor;

  const textY = boxY + textHeight / 2 + paddingVertical + textHeight * 0.11; // Text seems to be slightly too high.

  ctx.fillText(labelText, point.x, textY);
  if (isSelected) {
    const padded = rectWithPadding(rect, 10);
    drawSelectedRect(padded, ctx);
  }

  ctx.restore(); // Restore canvas state

  return { rect, isUnderCursor: isHighlighted };
};

// UTILITY

export const labelRectInCanvas = (
  label: StaticLabel,
  ctx: CanvasRenderingContext2D,
  map: any
): Rect => {
  let size = labelSizeInContext(label, ctx);
  const point = label.position;
  const padding = label.style.padding ?? 0;
  const x = point.x - padding;
  const y = point.y - padding;
  return { x, y, width: size.width, height: size.height };
};

export const labelSizeInContext = (
  label: StaticLabel,
  ctx: CanvasRenderingContext2D
): { width: number; height: number } => {
  const fontSize = label.style.fontSize;
  const font = label.style.font;
  const padding = label.style.padding ?? 0;

  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const textMetrics = ctx.measureText(label.text);
  const textWidth = textMetrics.width;

  let textHeight = fontSize * 1.2;
  if (
    textMetrics.actualBoundingBoxAscent &&
    textMetrics.actualBoundingBoxDescent
  ) {
    textHeight =
      textMetrics.actualBoundingBoxAscent +
      textMetrics.actualBoundingBoxDescent;
  }
  const labelWidth = textWidth + padding * 2;
  const labelHeight = textHeight + padding * 2;

  return { width: labelWidth, height: labelHeight };
};

//TODO: check OffscreenCanvasRenderingContext2D for map rendering performance.

export const textSizeInContext = (
  text: string,
  font: string,
  fontSize: number,
  ctx: CanvasRenderingContext2D
) => {
  ctx.font = `${fontSize}px ${font}`;
  ctx.textBaseline = "middle";

  const textMetrics = ctx.measureText(text);
  const textWidth = textMetrics.width;

  let textHeight = fontSize * 1.2;
  if (
    textMetrics.actualBoundingBoxAscent &&
    textMetrics.actualBoundingBoxDescent
  ) {
    textHeight =
      textMetrics.actualBoundingBoxAscent +
      textMetrics.actualBoundingBoxDescent;
  }
  const labelWidth = textWidth;
  const labelHeight = textHeight;

  return { width: labelWidth, height: labelHeight };
};
